import React, {Fragment, useRef, useEffect, useState} from "react";
import Pagination from "../layout/Pagination";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faEdit, faEye, faCircleXmark, faFileInvoice, faDownload, faEnvelope, faCancel, faUndo, faClockRotateLeft} from "@fortawesome/free-solid-svg-icons";

const getFunctionArgs = (paramKeys, object) => {
    return paramKeys.map(key => {
        const nestedKeys = key.split('.');
        let value = object;
        nestedKeys.forEach(nestedKey => {
            value = value[nestedKey];
        });
        return value;
    });
}

const ActionIcon = ({icon, title, tone, onClick}) => (
    <div
        className={`hover:cursor-pointer rounded-full p-2 transition-colors ${tone === 'danger' ? 'text-gray-500 hover:bg-red-50 hover:text-red-600' : 'text-gray-500 hover:bg-gray-100 hover:text-gray-900'}`}
        title={title}
        onClick={onClick}
    >
        <FontAwesomeIcon icon={icon} className="text-base"/>
    </div>
);

const ListEdit = (props) => {
    const headerCheckboxRef = useRef(null);

    const checkboxEnabled = !!props.checkbox;
    const checkboxKey = checkboxEnabled ? props.columns.functionParams[0] : null;

    const [selectedIds, setSelectedIds] = useState([]);

    const visibleIds = checkboxEnabled
        ? props.items.map(item => item[checkboxKey])
        : [];
    const selectedCount = checkboxEnabled
        ? visibleIds.filter(id => selectedIds.includes(id)).length
        : 0;
    const allSelected = checkboxEnabled && visibleIds.length > 0 && selectedCount === visibleIds.length;
    const someSelected = checkboxEnabled && selectedCount > 0 && !allSelected;

    useEffect(() => {
        if (headerCheckboxRef.current) {
            headerCheckboxRef.current.indeterminate = someSelected;
        }
    }, [someSelected]);

    const handleHeaderCheckboxChange = () => {
        if (allSelected) {
            setSelectedIds(prev => prev.filter(id => !visibleIds.includes(id)));
        } else {
            setSelectedIds(prev => [...new Set([...prev, ...visibleIds])]);
        }
    };

    const handleRowCheckboxChange = (id) => {
        setSelectedIds(prev =>
            prev.includes(id) ? prev.filter(existingId => existingId !== id) : [...prev, id]
        );
    };

    const hasActions = props.columns
        && props.columns.functionParams
        && (
            (props.handleEditIconClick)
            || (props.handleShowIconClick)
            || (props.handleUsagesIconClick)
            || (props.handleDeleteIconClick)
            || (props.handleCancelIconClick)
            || (props.handleInvoiceIconClick)
            || (props.handleDownloadIconClick)
            || (props.handleEnvelopeIconClick)
            || (props.handleUncancelIconClick)
        );

    return (
        <Fragment>
            {checkboxEnabled && (
                <div className="flex justify-end mb-3">
                    <button
                        disabled={selectedIds.length === 0}
                        onClick={() => props.checkbox.buttonHandler(selectedIds)}
                        className="px-4 py-2 rounded-lg text-sm font-semibold bg-pink-600 text-white shadow-sm hover:bg-pink-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {props.checkbox.buttonTitle} ({selectedIds.length})
                    </button>
                </div>
            )}
            <div className="bg-white rounded-2xl shadow-sm ring-1 ring-gray-950/5 overflow-hidden my-4">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="bg-gray-50 border-b border-gray-200">
                                {checkboxEnabled && (
                                    <th className="p-3 px-5 w-10">
                                        <input
                                            type="checkbox"
                                            ref={headerCheckboxRef}
                                            checked={allSelected}
                                            onChange={handleHeaderCheckboxChange}
                                            className="cursor-pointer rounded border-gray-300 text-pink-600 focus:ring-pink-500"
                                        />
                                    </th>
                                )}
                                {props.headers.map((header, index) => (
                                    <th
                                        key={index}
                                        className={`p-3 px-5 text-xs font-semibold uppercase tracking-wide text-gray-500 ${header === 'Actions' ? 'text-center' : 'text-left'}`}
                                    >
                                        {header}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {
                                props.items.map((item, index) => {
                                    let args = getFunctionArgs(props.columns.functionParams, item);

                                    return (
                                        <tr key={index} className="hover:bg-gray-50 transition-colors">
                                            {checkboxEnabled && (
                                                <td className="p-2 px-5 w-10">
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedIds.includes(item[checkboxKey])}
                                                        onChange={() => handleRowCheckboxChange(item[checkboxKey])}
                                                        className="cursor-pointer rounded border-gray-300 text-pink-600 focus:ring-pink-500"
                                                    />
                                                </td>
                                            )}
                                            {
                                                props.columns
                                                && props.columns.dataKeys
                                                && props.columns.dataKeys.map((dataKey, index) => (
                                                    <td key={index} className="p-3 px-5 text-gray-700">{item[dataKey]}</td>
                                                ))
                                            }
                                            {
                                                hasActions &&
                                                <td className="p-2 px-5">
                                                    <div className="flex justify-evenly items-center">
                                                        {props.handleEditIconClick && (
                                                            <ActionIcon icon={faEdit} title="Edit" onClick={() => props.handleEditIconClick(...args)}/>
                                                        )}
                                                        {props.handleShowIconClick && (
                                                            <ActionIcon icon={faEye} title="View" onClick={() => props.handleShowIconClick(...args)}/>
                                                        )}
                                                        {props.handleUsagesIconClick && (
                                                            <ActionIcon icon={faClockRotateLeft} title="Usages" onClick={() => props.handleUsagesIconClick(...args)}/>
                                                        )}
                                                        {props.handleDeleteIconClick && (
                                                            <ActionIcon icon={faCircleXmark} title="Delete" tone="danger" onClick={() => props.handleDeleteIconClick(...args)}/>
                                                        )}
                                                        {props.handleCancelIconClick && (
                                                            <ActionIcon icon={faCancel} title="Cancel" tone="danger" onClick={() => props.handleCancelIconClick(...args)}/>
                                                        )}
                                                        {props.handleUncancelIconClick && (
                                                            <ActionIcon icon={faUndo} title="Undo Cancellation" onClick={() => props.handleUncancelIconClick(...args)}/>
                                                        )}
                                                        {props.handleInvoiceIconClick && (
                                                            <ActionIcon icon={faFileInvoice} title="Make Invoice" onClick={() => props.handleInvoiceIconClick(...args)}/>
                                                        )}
                                                        {props.handleDownloadIconClick && (
                                                            <ActionIcon icon={faDownload} title="Download" onClick={() => props.handleDownloadIconClick(...args)}/>
                                                        )}
                                                        {props.handleEnvelopeIconClick && (
                                                            <ActionIcon icon={faEnvelope} title="Send Email" onClick={() => props.handleEnvelopeIconClick(...args)}/>
                                                        )}
                                                    </div>
                                                </td>
                                            }
                                        </tr>
                                    )
                                })
                            }
                        </tbody>
                    </table>
                </div>
                {props.pagination && (
                    <div className="px-5 py-3 border-t border-gray-100">
                        <Pagination
                            currentPage={props.pagination.currentPage}
                            itemsPerPage={props.pagination.perPage}
                            totalItems={props.pagination.total}
                            onPageChange={props.handlePageChange}
                            onPageSizeChange={props.handlePageSizeChange}
                        />
                    </div>
                )}
            </div>
        </Fragment>
    );
}

export default ListEdit;
